// Component library for authoring text presets. Each function returns a
// fully-formed PresetLayer with sensible defaults; pass an `overrides` object
// to tune anything. Used by:
//   - textPresets.ts (declarative preset definitions)
//   - the dev preset editor (each component is one entry in the palette)
//
// Adding a new component:
//   1. Write the factory function with defaults that produce a readable
//      "out of the box" layer (good position, fontSize, colors).
//   2. Register it in COMPONENT_CATALOG so the editor's palette picks it up.
//   3. Keep the factory pure — no external dependencies, no side effects.

import type {
  PresetLayer,
  PresetTextLayer,
  ShapeLayer,
} from './composeRender';

// ---------- Text components ----------

/**
 * Plain bold uppercase headline, top-left, no background.
 * Used in: Editorial CTA, Outline Caps, Side Banner.
 */
export const PlainHeadline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'headline',
  x: 0.06, y: 0.08, anchor: 'tl',
  maxWidthFrac: 0.9,
  textAlign: 'left',
  fontSize: 100,
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'uppercase',
  ...o,
});

/**
 * Huge headline (160px+), top-left, no background. Default lowercase.
 * Used in: Bold Statement, Spot Accent, Outline, Outline Caps.
 */
export const BigStatement = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'headline',
  x: 0.07, y: 0.09, anchor: 'tl',
  maxWidthFrac: 0.9,
  textAlign: 'left',
  fontSize: 160,
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'lowercase',
  ...o,
});

/**
 * Plain tagline (smaller subtitle), top-left, no background.
 * Stacks below the previous layer by default.
 * Used in: Editorial CTA, Outline, Outline Caps.
 */
export const PlainTagline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'tagline',
  x: 0.06, y: 0.18, anchor: 'tl',
  maxWidthFrac: 0.85,
  textAlign: 'left',
  fontSize: 36,
  fontWeight: '500',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'uppercase',
  stackOnPrevious: true,
  stackGap: 24,
  ...o,
});

/**
 * Larger tagline (60–70px) for bold-statement layouts. Positioned in the
 * lower half by default. Lowercase, no stacking.
 * Used in: Bold Statement.
 */
export const BoldTagline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'tagline',
  x: 0.07, y: 0.65, anchor: 'tl',
  maxWidthFrac: 0.5,
  textAlign: 'left',
  fontSize: 64,
  fontWeight: '600',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'lowercase',
  ...o,
});

/**
 * Hollow outline headline — transparent fill + clean black stroke around each
 * glyph. Uses Bebas Neue because bold sans fonts (Inter Bold etc.) have
 * complex glyph paths that pick up extra interior strokes when outlined;
 * Bebas Neue is a single-path display font designed for clean display
 * rendering. Uppercase-only by glyph design.
 * Used in: Outline, Outline Caps.
 */
export const OutlineHeadline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'headline',
  x: 0.07, y: 0.09, anchor: 'tl',
  maxWidthFrac: 0.9,
  textAlign: 'left',
  fontSize: 200,
  fontWeight: 'normal',
  fontFamily: 'Bebas Neue',
  color: 'transparent',
  textStrokeColor: '#000000',
  textStrokeWidth: 4,
  textTransform: 'uppercase',
  ...o,
});

/**
 * Headline with a hugging background (text-with-background pattern).
 * Default: white bg + black text + 32px padding.
 * Used in: Highlight Bars, Dark Bars (inverted), Side Banner, Offset Yellow/Cyan.
 */
export const HighlightedHeadline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'headline',
  x: 0.04, y: 0.10, anchor: 'tl',
  maxWidthFrac: 0.85,
  textAlign: 'left',
  fontSize: 77,
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'uppercase',
  backgroundColor: '#FFFFFF',
  backgroundPadding: 32,
  ...o,
});

/**
 * HighlightedHeadline with a built-in yellow offset shadow rect. The shadow
 * is drawn UNDER the white bg and grows with the text (it's a property of
 * the text layer, not a separate shape). Default offset: 12px right, 12px
 * down. Override the color/offset via the `offsetShadow` field.
 * Used in: Offset Yellow.
 */
export const HighlightedHeadlineOffset = (o: Partial<PresetTextLayer> = {}): PresetTextLayer =>
  HighlightedHeadline({
    offsetShadow: { color: '#F2FF00', offsetX: 12, offsetY: 12 },
    ...o,
  });

/**
 * Tagline with a hugging background. Stacks below the previous layer by
 * default with a 24px gap.
 * Used in: Highlight Bars, Dark Bars, Side Banner, Spot Accent, Offset.
 */
export const HighlightedTagline = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'tagline',
  x: 0.04, y: 0.24, anchor: 'tl',
  maxWidthFrac: 0.85,
  textAlign: 'left',
  fontSize: 40,
  fontWeight: '600',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'uppercase',
  backgroundColor: '#FFFFFF',
  backgroundPadding: 32,
  stackOnPrevious: true,
  stackGap: 24,
  ...o,
});

/**
 * HighlightedTagline with a built-in yellow offset shadow rect. Same behaviour
 * as HighlightedHeadlineOffset — shadow auto-grows with the text.
 * Used in: Offset Yellow.
 */
export const HighlightedTaglineOffset = (o: Partial<PresetTextLayer> = {}): PresetTextLayer =>
  HighlightedTagline({
    offsetShadow: { color: '#F2FF00', offsetX: 12, offsetY: 12 },
    ...o,
  });

// ---------- CTAs ----------

// Standard CTA anchor positions (anchor = 'mc', so x/y is the button center).
export const BR_CTA = { x: 0.687, y: 0.7738 };   // bottom-right
export const BL_CTA = { x: 0.2079, y: 0.8546 };  // bottom-left
export const BC_CTA = { x: 0.5, y: 0.8546 };     // bottom-center
export const OUTLINE_CTA = { x: 0.804, y: 0.8804 }; // Outline preset's spot

/**
 * Dark pill CTA — white text on a dark background. The most-used CTA style.
 * Default position: bottom-right.
 * Used in: 8 of the current 10 presets.
 */
export const DarkCTA = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'cta',
  staticText: 'SHOP NOW',
  x: BR_CTA.x, y: BR_CTA.y, anchor: 'mc',
  maxWidthFrac: 0.3,
  textAlign: 'center',
  fontSize: 36,
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#FFFFFF',
  textTransform: 'uppercase',
  backgroundColor: '#1C1C1C',
  backgroundPadding: 32,
  ...o,
});

/**
 * Light pill CTA — black text on white background. Inverse of DarkCTA.
 * Used in: Dark Bars.
 */
export const LightCTA = (o: Partial<PresetTextLayer> = {}): PresetTextLayer => ({
  kind: 'text',
  role: 'cta',
  staticText: 'SHOP NOW',
  x: BR_CTA.x, y: BR_CTA.y, anchor: 'mc',
  maxWidthFrac: 0.3,
  textAlign: 'center',
  fontSize: 36,
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#000000',
  textTransform: 'uppercase',
  backgroundColor: '#FFFFFF',
  backgroundPadding: 32,
  ...o,
});

// ---------- Decorative shapes ----------

/**
 * Decorative circle. Default: top-left, medium size, accent green.
 * Used in: Spot Accent.
 *
 * Note: x/y is the TOP-LEFT of the bounding box, not the circle's center.
 */
export const CircleAccent = (o: Partial<ShapeLayer> = {}): ShapeLayer => ({
  kind: 'shape',
  shape: 'circle',
  x: 0.05, y: 0.10,
  w: 0.25, h: 0.25,
  color: '#DFFE6D',
  opacity: 1,
  ...o,
});

// ---------- Component catalog (editor metadata) ----------

// What the preset editor's palette uses to render the "Add component" list.
// Each entry: id, display name, group (for organizing the palette), factory.
// The factory takes no args so the editor can drop one straight onto the
// canvas — users tune positions by dragging.

export type CatalogEntry =
  | { id: string; name: string; group: 'text' | 'cta' | 'shape'; kind: 'single'; factory: () => PresetLayer }
  | { id: string; name: string; group: 'text' | 'cta' | 'shape'; kind: 'multi'; factory: () => PresetLayer[] };

export const COMPONENT_CATALOG: CatalogEntry[] = [
  // Text — headlines
  { id: 'PlainHeadline',             name: 'Plain Headline',             group: 'text',  kind: 'single', factory: () => PlainHeadline() },
  { id: 'BigStatement',              name: 'Big Statement',              group: 'text',  kind: 'single', factory: () => BigStatement() },
  { id: 'OutlineHeadline',           name: 'Outline Headline',           group: 'text',  kind: 'single', factory: () => OutlineHeadline() },
  { id: 'HighlightedHeadline',       name: 'Highlighted Headline',       group: 'text',  kind: 'single', factory: () => HighlightedHeadline() },
  { id: 'HighlightedHeadlineOffset', name: 'Highlighted Headline Offset', group: 'text',  kind: 'single', factory: () => HighlightedHeadlineOffset() },
  // Text — taglines
  { id: 'PlainTagline',              name: 'Plain Tagline',              group: 'text',  kind: 'single', factory: () => PlainTagline() },
  { id: 'BoldTagline',               name: 'Bold Tagline',               group: 'text',  kind: 'single', factory: () => BoldTagline() },
  { id: 'HighlightedTagline',        name: 'Highlighted Tagline',        group: 'text',  kind: 'single', factory: () => HighlightedTagline() },
  { id: 'HighlightedTaglineOffset',  name: 'Highlighted Tagline Offset', group: 'text',  kind: 'single', factory: () => HighlightedTaglineOffset() },
  // CTAs
  { id: 'DarkCTA',                   name: 'Dark CTA',                   group: 'cta',   kind: 'single', factory: () => DarkCTA() },
  { id: 'LightCTA',                  name: 'Light CTA',                  group: 'cta',   kind: 'single', factory: () => LightCTA() },
  // Shapes
  { id: 'CircleAccent',              name: 'Circle Accent',              group: 'shape', kind: 'single', factory: () => CircleAccent() },
];
